/**
 * Détection d'une nouvelle version de l'app déployée.
 *
 * Une app installée sur l'écran d'accueil ne recharge jamais son code tant qu'on
 * navigue à l'intérieur : elle garde en mémoire celui de son dernier vrai démarrage,
 * parfois depuis des jours. Le service worker (`public/sw.js`) ne sert qu'aux push et
 * ne change presque jamais : le surveiller ne dit rien de l'app. Constaté le 15/10 :
 * un correctif en ligne depuis des heures, le téléphone affichait encore l'ancien
 * questionnaire, et « Rechercher une mise à jour » répondait « déjà à jour ».
 *
 * Chaque build publie `/version.json` (plugin dans vite.config.ts) avec la même date
 * de build que `__BUILD_DATE__`, compilée dans le code.
 */

const RELOADED_FOR_KEY = "zeyone_reloaded_for_build"

/**
 * Date de build de la version en ligne si elle est PLUS RÉCENTE que le code en mémoire
 * et qu'on n'a pas déjà rechargé pour elle (pas de boucle si le rechargement ne suffit
 * pas, ni si un `version.json` encore ancien est servi pendant un déploiement).
 */
export async function findNewerBuild(): Promise<string | null> {
  if (import.meta.env.DEV) return null
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" })
    if (!res.ok) return null
    // Fichier absent : Cloudflare renvoie index.html (repli SPA) → json() lève.
    const { build } = (await res.json()) as { build?: string }
    if (typeof build !== "string" || build <= __BUILD_DATE__) return null
    if (readReloadedFor() === build) return null
    return build
  } catch {
    return null
  }
}

/** Recharge l'app sur la version `build` (une seule tentative par version). */
export function reloadForBuild(build: string): void {
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, build)
  } catch {
    // stockage indisponible : on recharge quand même
  }
  window.location.reload()
}

function readReloadedFor(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_FOR_KEY)
  } catch {
    return null
  }
}
